import { Link } from "@tanstack/react-router";
import { PageHero } from "@/components/page-hero";
import { AccreditationsStrip } from "@/components/accreditations-strip";
import { LocationArticle } from "@/components/location-article";
import { LocationMap } from "@/components/location-map";
import { LocationFactsTable } from "@/components/location-facts-table";
import { cityFacts } from "@/lib/location-facts";
import { LocationGlossary } from "@/components/location-glossary";
import { SiblingCities } from "@/components/sibling-cities";
import { ReadingProgress } from "@/components/reading-progress";
import { LocationLeadForm } from "@/components/location-lead-form";
import { LocationFeedback } from "@/components/location-feedback";
import { findCounty, locationPath, countyPath } from "@/lib/locations";
import type { CityPageData } from "@/lib/city-page-data";

export function CityNotFound() {
  return (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="font-serif text-3xl text-brand">Location not yet live</h1>
      <p className="mt-4 text-muted-foreground">
        This location is scheduled for a future release phase. In the meantime, contact our team for HR support anywhere in the UK.
      </p>
      <Link to="/contact-hr-experts" className="mt-6 inline-block text-accent underline">
        Talk to an HR expert
      </Link>
    </div>
  );
}

export function CityPage({ data }: { data: CityPageData }) {
  const { location, heroTitle, heroIntro, body } = data;
  const path = locationPath(location);
  // Counties named after their region are not published as their own hub, so
  // the breadcrumb skips straight from region to town.
  const flattened = location.countySlug === location.regionSlug;
  const breadcrumbs = [
    { label: "Home", path: "/" },
    { label: "Locations", path: "/hr-outsourcing-services" },
    { label: location.region, path: `/hr-outsourcing-services/${location.regionSlug}` },
    ...(flattened
      ? []
      : [{ label: location.county, path: countyPath(location.regionSlug, location.countySlug) }]),
    { label: location.city, path },
  ];
  return (
    <>
      <ReadingProgress />
      <PageHero
        eyebrow={flattened ? location.region : `${location.region} · ${location.county}`}
        title={heroTitle}
        subtitle={`Fixed-fee HR support for ${location.city} employers`}
        breadcrumbs={breadcrumbs}
        breadcrumbSchema={false}
        actions={
          <Link
            to="/contact-hr-experts"
            className="inline-flex items-center justify-center rounded-md bg-accent px-5 py-2.5 text-sm font-medium text-accent-foreground hover:brightness-110"
          >
            Request a call-back
          </Link>
        }
        aside={
          <div className="lg:border-l lg:border-border/60 lg:pl-8">
            <p className="font-serif text-lg italic leading-relaxed text-brand/80">
              &ldquo;{heroIntro}&rdquo;
            </p>
            <div className="mt-6 flex items-center gap-3 border-t border-border/60 pt-5">
              <div
                aria-hidden="true"
                className="flex size-10 shrink-0 items-center justify-center rounded-full bg-brand font-serif text-sm text-brand-foreground"
              >
                DW
              </div>
              <div className="min-w-0 text-sm">
                <p className="font-medium text-brand">Reviewed by David White</p>
                <p className="text-xs text-muted-foreground">
                  Director · MCIPD, BA (Hons), MA Strategic HRM
                </p>
              </div>
            </div>
          </div>
        }
      />
      <LocationFactsTable
        place={location.city}
        note={`Coverage, employer density and typical fixed-fee retainer for ${location.city} businesses.`}
        facts={cityFacts(
          location,
          findCounty(location.regionSlug, location.countySlug)?.cities.length ?? 1,
        )}
      />
      <LocationArticle
        body={body}
        location={location.city}
        accreditations={<AccreditationsStrip />}
        openingAside={<LocationGlossary variant="hero" />}
        mapSlot={
          <LocationMap
            query={`${location.city}, ${location.county}, United Kingdom`}
            label={location.city}
          />
        }
      />

      <SiblingCities
        county={location.county}
        countySlug={location.countySlug}
        regionSlug={location.regionSlug}
        currentCitySlug={location.citySlug}
        cities={findCounty(location.regionSlug, location.countySlug)?.cities ?? []}
      />
      <LocationLeadForm location={location.city} />
      <LocationFeedback location={location.city} path={path} />
    </>
  );
}
